import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import axios from "axios";
import Papa from "papaparse";
import { toast } from "react-hot-toast";
import { Table, Badge, Modal, Form, Button as BsButton } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { AllClasses } from "../../configs/allClasses";
import { useCurrentTermAndSession } from "../../hooks/useCurrentTermAndSession";

// Helper: resolve class code → human readable name
const getClassName = (code) => {
  const found = AllClasses.find((c) => c.code === code);
  return found ? found.name : code;
};

export function AssignOtherFees() {
  const { currentSession, currentTerm } = useCurrentTermAndSession();
  const [isInitialized, setIsInitialized] = useState(false);

  const [session, setSession] = useState("");
  const [term, setTerm] = useState("");

  useEffect(() => {
    if (currentSession && currentTerm && !isInitialized) {
      setSession(currentSession);
      setTerm(currentTerm);
      setIsInitialized(true);
    }
  }, [currentSession, currentTerm, isInitialized]);

  // Data State
  const [students, setStudents] = useState([]);
  const [feeItems, setFeeItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  // Arrears (BillAdjustment debits) per student, keyed by student _id
  const [arrearsMap, setArrearsMap] = useState({});
  const [loading, setLoading] = useState(false);

  // Search/Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [classFilter, setClassFilter] = useState("All");

  // Multi-select students (Manual Assignment)
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // CSV Bulk Upload State
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const fileInputRef = useRef(null);

  // Form State - supports multiple fee rows
  const [selectedFees, setSelectedFees] = useState([
    { feeCode: "", amount: "" },
  ]);

  const appUrl = process.env.REACT_APP_URL || "http://localhost:4000/api/v1";
  const token = localStorage.getItem("token");

  // Wrap fetchers in useCallback to fix eslint warnings
  const fetchStudents = React.useCallback(async () => {
    try {
      const res = await axios.get(
        `${appUrl}/users?role=student&status=active&limit=5000`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setStudents(res.data.data.list || res.data.data || []);
    } catch (error) {
      console.error("Error fetching students", error);
    }
  }, [appUrl, token]);

  const fetchFeeItems = React.useCallback(async () => {
    try {
      const res = await axios.get(`${appUrl}/fee-items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const otherFees = res.data.data.filter(
        (f) => f.category === "Other" || f.category === "Arrears",
      );
      setFeeItems(otherFees);
    } catch (error) {
      console.error("Error fetching fee items", error);
    }
  }, [appUrl, token]);

  const fetchAssignments = React.useCallback(async () => {
    try {
      const res = await axios.get(
        `${appUrl}/fee-assignments?session=${session}&term=${term}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAssignments(res.data.data);
    } catch (error) {
      console.error("Error fetching assignments", error);
    }
  }, [appUrl, token, session, term]);

  // Arrears are stored as BillAdjustment debits (not FeeAssignments), so we
  // fetch each student's bill adjustments and surface them in the right-hand
  // panel — otherwise arrears would be invisible on this page.
  const fetchArrearsMap = React.useCallback(async () => {
    try {
      const res = await axios.get(
        `${appUrl}/bills?session=${session}&term=${term}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const bills = res.data.data || [];

      const map = {};
      await Promise.all(
        bills.map(async (bill) => {
          try {
            const adjRes = await axios.get(
              `${appUrl}/bill-adjustments/${bill._id}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const adjustments = adjRes.data.data || [];
            const arrears = adjustments.filter(
              (adj) =>
                adj.type === "debit" &&
                /arrears|outstanding/i.test(adj.description || ""),
            );
            const key =
              typeof bill.studentId === "object"
                ? bill.studentId?._id
                : bill.studentId;
            if (key && arrears.length) {
              map[key] = arrears.map((a) => ({
                _id: a._id,
                description: a.description,
                amount: a.amount,
              }));
            }
          } catch (e) {
            // One bill failing shouldn't block the rest
          }
        }),
      );

      setArrearsMap(map);
    } catch (error) {
      console.error("Error fetching arrears adjustments", error);
    }
  }, [appUrl, token, session, term]);

  useEffect(() => {
    fetchStudents();
    fetchFeeItems();
  }, [fetchStudents, fetchFeeItems]);

  useEffect(() => {
    if (isInitialized && session && term) {
      fetchAssignments();
      fetchArrearsMap();
    }
  }, [fetchAssignments, fetchArrearsMap, isInitialized, session, term]);

  // Toggle a student in / out of the selected set
  const toggleStudent = (student) => {
    setSelectedStudents((prev) => {
      const alreadySelected = prev.find((s) => s._id === student._id);
      if (alreadySelected) {
        return prev.filter((s) => s._id !== student._id);
      }
      return [...prev, student];
    });
  };

  const isSelected = (id) => selectedStudents.some((s) => s._id === id);

  const handleOpenAssign = () => {
    if (selectedStudents.length === 0) {
      toast.error("Please select at least one student first.");
      return;
    }
    const defaultFeeCode = feeItems.length > 0 ? feeItems[0].feeCode : "";
    setSelectedFees([{ feeCode: defaultFeeCode, amount: "" }]);
    setShowModal(true);
  };

  const addFeeRow = () => {
    const defaultFeeCode = feeItems.length > 0 ? feeItems[0].feeCode : "";
    setSelectedFees([...selectedFees, { feeCode: defaultFeeCode, amount: "" }]);
  };

  const removeFeeRow = (index) => {
    const updated = [...selectedFees];
    updated.splice(index, 1);
    setSelectedFees(updated);
  };

  const handleFeeChange = (index, field, value) => {
    const updated = [...selectedFees];
    updated[index][field] = value;
    setSelectedFees(updated);
  };

  const handleAssignFee = async (e) => {
    e.preventDefault();
    setLoading(true);

    const validFees = selectedFees.filter(
      (f) => f.feeCode && Number(f.amount) > 0,
    );

    if (validFees.length === 0) {
      toast.error("Please add at least one valid fee with an amount.");
      setLoading(false);
      return;
    }

    try {
      // For each selected student × each valid fee, fire an API call
      const allRequests = [];
      for (const student of selectedStudents) {
        for (const fee of validFees) {
          allRequests.push(
            axios.post(
              `${appUrl}/fee-assignments`,
              {
                studentId: student._id,
                feeCode: fee.feeCode,
                session,
                term,
                amount: Number(fee.amount),
              },
              { headers: { Authorization: `Bearer ${token}` } },
            ),
          );
        }
      }

      await Promise.all(allRequests);

      toast.success(
        `Fees assigned to ${selectedStudents.length} student(s) successfully!`,
      );
      setShowModal(false);
      setSelectedStudents([]);
      setSelectedFees([{ feeCode: "", amount: "" }]);
      fetchAssignments();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to assign fees");
    } finally {
      setLoading(false);
    }
  };

  // SAFELY RESOLVE STUDENT NAME OR ADMISSION NUMBER FROM ASSIGNMENT
  const handleDeleteAssignment = async (id) => {
    if (!window.confirm("Are you sure you want to remove this assigned fee?"))
      return;
    try {
      await axios.delete(`${appUrl}/fee-assignments/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Fee assignment removed");
      fetchAssignments();
    } catch (error) {
      toast.error("Failed to remove assignment");
    }
  };

  // ─── CSV UPload Logic ────────────────────────────────────────────────────────

  const handleDownloadTemplate = () => {
    if (feeItems.length === 0) {
      toast.error("No active optional fees available to generate a template.");
      return;
    }
    // Base columns
    const headers = ["admissionNumber", ...feeItems.map((f) => f.description)];

    // Create an empty dummy row to guide the user
    const dummyRow = ["FG/2024/001"];
    feeItems.forEach(() => dummyRow.push("")); // Empty strings for fee prices

    const csvContent = [headers.join(","), dummyRow.join(",")].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute(
      "download",
      `Custom_Fees_Template_${session.replace("/", "-")}_${term}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvUpload = async (file) => {
    if (!file) return;

    setCsvUploading(true);
    const toastId = toast.loading("Processing CSV...");

    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const rows = results.data;

          if (rows.length < 2) {
            toast.error("CSV file is empty or missing data rows.", {
              id: toastId,
            });
            setCsvUploading(false);
            return;
          }

          const dataRows = rows.slice(1); // Skip the header row
          const mappedData = [];

          for (const row of dataRows) {
            // Index 0 is strictly Admission Number
            const admNo = row[0]?.trim();
            if (!admNo) continue;

            const assignments = [];

            // Index 1 onwards aligns with feeItems[0] onwards (same as template)
            for (let i = 1; i < row.length; i++) {
              const val = row[i];
              const fee = feeItems[i - 1]; // The feeItem for this column

              if (!fee || val === undefined || val === null) continue;

              const parsedVal = Number(String(val).replace(/,/g, ""));
              if (!isNaN(parsedVal) && parsedVal > 0) {
                assignments.push({ feeCode: fee.feeCode, amount: parsedVal });
              }
            }

            if (assignments.length > 0) {
              mappedData.push({ admissionNumber: admNo, assignments });
            }
          }

          if (mappedData.length === 0) {
            toast.error("No valid fee assignments found in the file.", {
              id: toastId,
            });
            setCsvUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
          }

          // Show Preview Modal instead of uploading immediately
          toast.dismiss(toastId);
          setCsvPreviewData(mappedData);
          setShowCsvPreview(true);
          setCsvUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (error) {
          toast.error(error?.message || "Error processing CSV upload.", {
            id: toastId,
          });
          setCsvUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      },
      error: () => {
        toast.error("Failed to parse the CSV file.", { id: toastId });
        setCsvUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      },
    });
  };

  const submitCsvData = async () => {
    setLoading(true);
    const toastId = toast.loading("Uploading fee assignments...");
    try {
      const res = await axios.post(
        `${appUrl}/fee-assignments/bulk-csv`,
        { data: csvPreviewData, session, term },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const { matchedStudents, assignmentsProcessed, arrearsProcessed } = res.data.data;

      let msg = `Processed ${matchedStudents} students (${assignmentsProcessed} custom fees)`;
      if (arrearsProcessed > 0) {
        msg += ` and applied ${arrearsProcessed} arrears to bills!`;
      } else {
        msg += "!";
      }

      toast.success(msg, { id: toastId });

      setShowCsvPreview(false);
      setCsvPreviewData([]);
      fetchAssignments();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error submitting CSV upload.",
        { id: toastId },
      );
    } finally {
      setLoading(false);
    }
  };

  // Safely resolve student name from a populated or flat ID field
  const getStudentName = (id) => {
    if (typeof id === "object" && id?.firstName) {
      return `${id.firstName} ${id.lastName} (${getClassName(id.currentClass)})`;
    }
    const s = students.find((x) => x._id === id);
    return s
      ? `${s.firstName} ${s.lastName} (${getClassName(s.currentClass)})`
      : String(id);
  };

  // Group assignments by student so each student appears once,
  // merging in any arrears adjustments (BillAdjustment debits) per student.
  const groupedAssignments = useMemo(() => {
    const map = {};
    assignments.forEach((a) => {
      const key =
        typeof a.studentId === "object" ? a.studentId._id : a.studentId;
      if (!map[key]) {
        map[key] = { studentRef: a.studentId, fees: [], arrears: [] };
      }
      map[key].fees.push(a);
    });
    Object.entries(arrearsMap).forEach(([studentId, arrears]) => {
      if (!map[studentId]) {
        const s = students.find((x) => x._id === studentId);
        map[studentId] = { studentRef: s || { _id: studentId }, fees: [], arrears: [] };
      }
      map[studentId].arrears = arrears;
    });
    return Object.values(map);
  }, [assignments, arrearsMap, students]);

  const getFeeName = (code) => {
    const f = feeItems.find((x) => x.feeCode === code);
    return f ? f.description : code;
  };

  // Unique classes present among fetched students (using codes + resolved names)
  const uniqueClasses = useMemo(() => {
    const codes = new Set(students.map((s) => s.currentClass).filter(Boolean));
    return Array.from(codes)
      .map((code) => ({ code, name: getClassName(code) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [students]);

  // Filtered student list
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const matchesClass =
        classFilter === "All" || s.currentClass === classFilter;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        s.firstName?.toLowerCase().includes(q) ||
        s.lastName?.toLowerCase().includes(q) ||
        s.admissionNumber?.toLowerCase().includes(q);
      return matchesClass && matchesSearch;
    });
  }, [students, searchQuery, classFilter]);

  return (
    <Container>
      <Header>
        <div className="title-row">
          <div>
            <h3>Assign Additional Fees</h3>
            <p>
              Assign optional fees (Bus, Hostel, PTA, etc.) to specific
              students.
            </p>
          </div>
          <div className="action-btns">
            <SmallActionBtn
              onClick={handleDownloadTemplate}
              title="Download CSV Template"
            >
              <Icon icon="mdi:file-download-outline" width="18" />
              Template
            </SmallActionBtn>
            <BsButton
              variant="outline-primary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={csvUploading}
            >
              <Icon
                icon={csvUploading ? "mdi:loading" : "mdi:upload"}
                width="18"
                className={csvUploading ? "spin" : ""}
              />
              {csvUploading ? "Uploading..." : "Upload CSV"}
            </BsButton>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".csv"
              onChange={(e) => handleCsvUpload(e.target.files[0])}
            />
          </div>
        </div>
      </Header>

      <Controls>
        <SelectGroup>
          <label>Academic Session</label>
          <select value={session} onChange={(e) => setSession(e.target.value)}>
            <option value="2023/2024">2023/2024</option>
            <option value="2024/2025">2024/2025</option>
            <option value="2025/2026">2025/2026</option>
            <option value="2026/2027">2026/2027</option>
            <option value="2027/2028">2027/2028</option>
            <option value="2028/2029">2028/2029</option>

          </select>
        </SelectGroup>

        <SelectGroup>
          <label>Term</label>
          <select value={term} onChange={(e) => setTerm(e.target.value)}>
            <option value="FIRST TERM">FIRST TERM</option>
            <option value="SECOND TERM">SECOND TERM</option>
            <option value="THIRD TERM">THIRD TERM</option>
          </select>
        </SelectGroup>
      </Controls>

      <ContentGrid>
        {/* Left Col: Student Picker */}
        <Card>
          <CardHeader>
            <h4>1. Select Students</h4>
            {selectedStudents.length > 0 && (
              <AssignBtn onClick={handleOpenAssign}>
                <Icon icon="mdi:plus-circle" width="18" />
                Assign Fees to {selectedStudents.length} student
                {selectedStudents.length > 1 ? "s" : ""}
              </AssignBtn>
            )}
          </CardHeader>

          <StudentFilters>
            <SearchInput
              type="text"
              placeholder="Search by name or admission no..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="All">All Classes</option>
              {uniqueClasses.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </StudentFilters>

          <FilterStats>
            Showing {filteredStudents.length} of {students.length} students
            {selectedStudents.length > 0 && (
              <SelectedPill>
                {selectedStudents.length} selected
                <button onClick={() => setSelectedStudents([])}>✕ Clear</button>
              </SelectedPill>
            )}
          </FilterStats>

          <ScrollableList>
            {students.length === 0 && (
              <p style={{ color: "#999", padding: "10px" }}>
                Loading students...
              </p>
            )}
            {filteredStudents.map((s) => (
              <StudentItem
                key={s._id}
                isSelected={isSelected(s._id)}
                onClick={() => toggleStudent(s)}
              >
                <CheckBox isSelected={isSelected(s._id)}>
                  {isSelected(s._id) && (
                    <Icon icon="mdi:check" color="white" width="14" />
                  )}
                </CheckBox>
                <div style={{ flex: 1 }}>
                  <strong>
                    {s.firstName} {s.lastName}
                  </strong>
                  <br />
                  <small>
                    {s.admissionNumber} • {getClassName(s.currentClass)}
                  </small>
                </div>
              </StudentItem>
            ))}
            {filteredStudents.length === 0 && students.length > 0 && (
              <EmptyState style={{ padding: "20px" }}>
                No students match your search.
              </EmptyState>
            )}
          </ScrollableList>
        </Card>

        {/* Right Col: Assigned Bills */}
        <Card>
          <h4>
            Assigned Bills ({session} – {term})
          </h4>
          {groupedAssignments.length === 0 ? (
            <EmptyState>No optional fees assigned for this term.</EmptyState>
          ) : (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th style={{ width: "30%" }}>Student</th>
                  <th>Assigned Fees</th>
                  <th style={{ width: "15%" }}>Total (₦)</th>
                </tr>
              </thead>
              <tbody>
                {groupedAssignments.map((group, gi) => {
                  const feesTotal = group.fees.reduce(
                    (sum, f) => sum + f.amount,
                    0,
                  );
                  const arrearsTotal = group.arrears.reduce(
                    (sum, a) => sum + a.amount,
                    0,
                  );
                  const total = feesTotal + arrearsTotal;
                  return (
                    <tr key={gi} style={{ verticalAlign: "middle" }}>
                      <td style={{ fontWeight: 500, verticalAlign: "middle" }}>
                        {getStudentName(group.studentRef)}
                      </td>
                      <td>
                        {group.fees.map((a) => (
                          <FeeTagRow key={a._id}>
                            <Badge bg="info">{getFeeName(a.feeCode)}</Badge>
                            <span style={{ fontSize: 13, color: "#444" }}>
                              ₦{a.amount.toLocaleString()}
                            </span>
                            <ActionBtn
                              onClick={() => handleDeleteAssignment(a._id)}
                              title="Remove this fee"
                            >
                              <Icon
                                icon="mdi:trash-can"
                                color="#d32f2f"
                                width="15"
                              />
                            </ActionBtn>
                          </FeeTagRow>
                        ))}
                        {group.arrears.map((a) => (
                          <FeeTagRow key={`ar-${a._id}`}>
                            <Badge
                              bg="danger"
                              style={{ cursor: "default" }}
                              title="This is an arrears adjustment, not a custom fee"
                            >
                              <Icon
                                icon="mdi:alert"
                                width="13"
                                style={{ marginRight: 4 }}
                              />
                              {a.description}
                            </Badge>
                            <span
                              style={{ fontSize: 13, color: "#d32f2f" }}
                            >
                              ₦{a.amount.toLocaleString()}
                            </span>
                          </FeeTagRow>
                        ))}
                      </td>
                      <td
                        style={{
                          fontWeight: 700,
                          color: "#d32f2f",
                          verticalAlign: "middle",
                        }}
                      >
                        ₦{total.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </ContentGrid>

      {/* Assignment Modal - Multi-student × multi-fee */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Assign Fees to {selectedStudents.length} Student
            {selectedStudents.length > 1 ? "s" : ""}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAssignFee}>
          <Modal.Body>
            {/* Show selected students as pills */}
            <SelectedStudentList>
              {selectedStudents.map((s) => (
                <StudentPill key={s._id}>
                  {s.firstName} {s.lastName}
                  <button onClick={() => toggleStudent(s)}>✕</button>
                </StudentPill>
              ))}
            </SelectedStudentList>

            <FeeFormContainer style={{ marginTop: "20px" }}>
              {selectedFees.map((fee, index) => (
                <FeeRow key={index}>
                  <div className="fee-select">
                    <label>Fee Type</label>
                    <select
                      value={fee.feeCode}
                      onChange={(e) =>
                        handleFeeChange(index, "feeCode", e.target.value)
                      }
                      required
                    >
                      {feeItems.map((f) => (
                        <option key={f.feeCode} value={f.feeCode}>
                          {f.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fee-amount">
                    <label>Amount (₦)</label>
                    <input
                      type="number"
                      min="0"
                      value={fee.amount}
                      onChange={(e) =>
                        handleFeeChange(index, "amount", e.target.value)
                      }
                      required
                    />
                  </div>
                  {selectedFees.length > 1 && (
                    <ActionBtn
                      type="button"
                      onClick={() => removeFeeRow(index)}
                      style={{ marginTop: 22 }}
                    >
                      <Icon
                        icon="mdi:close-circle"
                        color="#d32f2f"
                        width="20"
                      />
                    </ActionBtn>
                  )}
                </FeeRow>
              ))}
              <AddRowBtn type="button" onClick={addFeeRow}>
                <Icon icon="mdi:plus" /> Add another fee
              </AddRowBtn>
            </FeeFormContainer>
          </Modal.Body>
          <Modal.Footer>
            <BsButton variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </BsButton>
            <BsButton variant="primary" type="submit" disabled={loading}>
              {loading
                ? "Assigning..."
                : `Confirm & Assign to ${selectedStudents.length} Student${selectedStudents.length > 1 ? "s" : ""}`}
            </BsButton>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* CSV Preview Modal */}
      <Modal
        show={showCsvPreview}
        onHide={() => setShowCsvPreview(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Preview CSV Upload ({csvPreviewData.length} records)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <p style={{ fontSize: 14, color: "#666", marginBottom: 15 }}>
            Please review the mapped fee data before confirming. Only valid
            assignments are shown below.
          </p>
          <Table responsive hover size="sm" bordered>
            <thead style={{ background: "#f8f9fa" }}>
              <tr>
                <th>Admission No</th>
                <th>Mapped Fees</th>
                <th>Total (₦)</th>
              </tr>
            </thead>
            <tbody>
              {csvPreviewData.map((row, i) => {
                const total = row.assignments.reduce(
                  (sum, f) => sum + f.amount,
                  0,
                );
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{row.admissionNumber}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "6px",
                        }}
                      >
                        {row.assignments.map((a, j) => (
                          <Badge bg="info" key={j}>
                            {getFeeName(a.feeCode)}: ₦
                            {a.amount.toLocaleString()}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td style={{ fontWeight: 700, color: "#d32f2f" }}>
                      ₦{total.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <BsButton
            variant="secondary"
            onClick={() => setShowCsvPreview(false)}
          >
            Cancel
          </BsButton>
          <BsButton
            variant="primary"
            onClick={submitCsvData}
            disabled={loading}
          >
            {loading
              ? "Uploading..."
              : `Confirm & Upload ${csvPreviewData.length} records`}
          </BsButton>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

// ─── Styled Components ─────────────────────────────────────────────────────────
const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  margin-bottom: 25px;

  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  h3 {
    margin: 0;
    color: #333;
  }
  p {
    color: #666;
    margin-top: 5px;
    font-size: 14px;
  }

  .action-btns {
    display: flex;
    gap: 10px;
  }

  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 25px;
  background: white;
  padding: 15px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SelectGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  label {
    font-size: 14px;
    font-weight: 500;
    color: #444;
  }
  select {
    padding: 10px 15px;
    border-radius: 6px;
    border: 1px solid #ddd;
    min-width: 200px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 25px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  h4 {
    margin-top: 0;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
    font-size: 18px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  h4 {
    margin: 0;
    font-size: 18px;
  }
`;

const AssignBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #1558b0;
  }
`;

const StudentFilters = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  select {
    padding: 8px;
    border-radius: 6px;
    border: 1px solid #ddd;
    outline: none;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  outline: none;
`;

const FilterStats = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SelectedPill = styled.span`
  background: #e8f0fe;
  color: #1a73e8;
  border-radius: 20px;
  padding: 2px 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  button {
    background: none;
    border: none;
    color: #1a73e8;
    cursor: pointer;
    font-size: 11px;
    padding: 0;
    font-weight: bold;
  }
`;

const DividerText = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 30px 0 20px 0;
  color: #aaa;
  font-size: 13px;
  font-weight: bold;

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid #eee;
  }

  &::before {
    margin-right: 0.5em;
  }

  &::after {
    margin-left: 0.5em;
  }
`;

const CsvUploadArea = styled.div`
  background: #fdfdfd;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 20px;

  h4 {
    margin: 0 0 5px 0 !important;
    border: none !important;
    padding: 0 !important;
  }
  p {
    font-size: 13px;
    color: #666;
    margin: 0;
  }
`;

const FileUploadBox = styled.div`
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  border: 1px dashed #ddd;
  padding: 15px;
  border-radius: 8px;

  input[type="file"] {
    font-size: 14px;
    margin-bottom: 5px;
  }

  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`;

const SmallActionBtn = styled.button`
  background: white;
  border: 1px solid #ddd;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: 0.15s;

  &:hover {
    background: #f8f9fa;
    border-color: #ccc;
  }
`;

const ScrollableList = styled.div`
  max-height: 480px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }
`;

const StudentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 10px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  background: ${(p) => (p.isSelected ? "#e8f0fe" : "transparent")};
  border-left: 3px solid ${(p) => (p.isSelected ? "#1a73e8" : "transparent")};
  transition: 0.15s;

  &:hover {
    background: ${(p) => (p.isSelected ? "#dce8fd" : "#f9f9f9")};
  }

  strong {
    font-size: 14px;
    color: #333;
  }
  small {
    color: #888;
  }
`;

const CheckBox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${(p) => (p.isSelected ? "#1a73e8" : "#ccc")};
  background: ${(p) => (p.isSelected ? "#1a73e8" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: 0.15s;
`;

const FeeFormContainer = styled.div`
  background: #fdfdfd;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const AddRowBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #1a73e8;
  background: transparent;
  border: 1px dashed #1a73e8;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 10px;
  width: 100%;
  justify-content: center;

  &:hover {
    background: #f0f4fc;
  }
`;

const ActionBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  &:hover {
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #777;
  background: #f9f9f9;
  border-radius: 6px;
`;

const SelectedStudentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 6px;
  margin-bottom: 8px;
`;

const StudentPill = styled.div`
  background: #e8f0fe;
  color: #1a73e8;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  button {
    background: none;
    border: none;
    color: #888;
    cursor: pointer;
    font-size: 12px;
    padding: 0;
    &:hover {
      color: #d32f2f;
    }
  }
`;

const FeeRow = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-end;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #eee;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  margin-bottom: 8px;
  cursor: pointer;
  opacity: 0.7;
  &:hover {
    opacity: 1;
  }
`;

const AddMoreBtn = styled.button`
  background: none;
  border: 1px dashed #1a73e8;
  color: #1a73e8;
  padding: 10px;
  width: 100%;
  border-radius: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  margin-top: 10px;
  &:hover {
    background: #f4f8ff;
  }
`;

const FeeTagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px dotted #eee;
  &:last-child {
    border-bottom: none;
  }
`;
